import { World } from './Core/World';
import { SmoothScroll } from './Core/SmoothScroll';
import { Config } from './Config';
import { Camera } from './Core/Camera';
import { Renderer } from './Core/Renderer';
import Time from './Core/Time';
import { Sizes } from './Core/Sizes';
import EventEmitter from './Utils/EventEmitter';
import Resources from './Utils/Resources';
import { detectOperatingSystem, HoverClassOnParent, Modal } from '../js/classes/Helpers';
import { Newsletter } from '../js/classes/Newsletter';
import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import Sliders from './Core/Sliders';
import LazyLoader from './Core/LazyLoader';
import AppearAnimation from './Utils/AppearAnimation';

gsap.registerPlugin(ScrollToPlugin) 
/**
 * Application class
 * Main entry point for the application
 */
export class GameTheory extends EventEmitter {
  /**
   * Constructor
   */
  constructor(el) {
    super();
    // State
    this.initialized = false;
    this.ready = false;
    this.parent = el;

    // Debug mode
    this.debug = Config.debug || true;

    // Initialize immediately
    this.init();
  }

  /**
   * Initialize the application
   */
  init() {
    // Prevent multiple initializations
    if (this.initialized) return;
    this.initialized = true;

    this.bindAll();

    // Initialize core components
    this.time = Time.getInstance();
    this.sizes = Sizes.getInstance();
    this.camera = Camera.getInstance();
    this.renderer = Renderer.getInstance(this.parent);
    this.smoothScroll = SmoothScroll.getInstance();
    this.world = World.getInstance();
    this.resources = new Resources();
    this.smoothScroll.setRenderer(this.renderer);

    this.commonInit();
    this.initUI();
    // Track readiness of components
    let worldReady = false;
    let scrollReady = false;

    // Set up readiness checks
    const checkAllReady = () => {
      if (worldReady && scrollReady) {
        this.ready = true;
        if (this.debug) window.app = this;
        this.trigger('ready');
      }
    };

    // Listen for component ready events
    this.world.on('ready', () => {
      worldReady = true;
      checkAllReady();
    });

    this.smoothScroll.on('ready', () => {
      scrollReady = true;
      checkAllReady();
    });

    // Setup event listeners
    this.setupEventListeners();
  }

  bindAll() {
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onBeforeUnload = this.onBeforeUnload.bind(this);
    this.update = this.update.bind(this);
  }

  /**
   * Set up event listeners
   */
  setupEventListeners() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('beforeunload', this.onBeforeUnload);
    this.update();

    this.time.on('tick', () => {
      this.update();
    });

    this.camera.on('scroll', () => {
      this.world.logoUpdate();
      //this.renderer.render();
    });
  }

  update() {
    if (typeof this.world !== 'undefined' && this.world) {
      this.world.update(this.time.delta, this.time.elapsed);
    }
    this.smoothScroll.raf(this.time.elapsed * 1000);
    this.renderer.render();

    this.trigger('update');
  }

  commonInit() {
    this.newsletter = new Newsletter();
    const os = detectOperatingSystem();

    if (os === "Windows") {
      document.body.classList.add("win");
    }

    HoverClassOnParent('.footer-nav a', '.footer-nav');

    // Initialize lazy loader singleton
    this.lazyLoader = LazyLoader.getInstance();

    this.sliders = new Sliders();
    Modal();
    this.appearAnimation = new AppearAnimation({
      container: document.body,
    });

    setTimeout(() => {
      if (this.lazyLoader) {
        this.lazyLoader.loadInitialSlides(5);
      }
    }, 100);

    setTimeout(() => {
      if (this.lazyLoader) {
        this.lazyLoader.loadAllSliderImages();
      }
    }, 2000);

    setTimeout(() => {
      if (this.lazyLoader) {
        this.lazyLoader.loadAllSpeakerImages();
      }
    }, 3000);
  }

  initUI() {
    this.html = {};

    this.html.main = document.getElementById('main');

    gsap.to(this.html.main, {
      opacity: 1,
      duration: 0.2,
      ease: 'power2.inOut',
    });

    this.initNavigation();
  }

  initNavigation() {
    this.header = document.getElementById('header');
    this.sections = Array.from(document.querySelectorAll('.section-content'));
    this.navLinks = Array.from(document.querySelectorAll('.gt-navigation a'));
    this.colorMenuSections = Array.from(document.querySelectorAll('.color-menu'));

    this.navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('href').substring(1);
        const targetSection = document.getElementById(targetId);

        if (!targetSection) {
          console.error(`Target section not found: ${targetId}`);
          return;
        }

        // Get the offset for the target section
        let offset;

        if(this.sizes.isMobile) {
          offset = targetSection.dataset.scrollOffsetMobile || targetSection.dataset.scrollOffset || '0';
        } else {
          offset = targetSection.dataset.scrollOffset || '0';
        }

        if (offset.endsWith('%')) {
          const percentage = parseFloat(offset) / 100;
          offset = window.innerHeight * percentage;
        } else {
          offset = parseInt(offset);
        }


        // Scroll to the target section with the specified offset using GSAP
        gsap.to(window, {
          scrollTo: {
            y: targetSection,
            offsetY: parseInt(offset)
          },
          duration: 1,
          ease: "power3.inOut",
          onComplete: () => {
            // Ensure the smooth scroller is updated after animation
            if (this.scroller) {
              this.scroller.update();
            }
          }
        });
      });
    });
  }

  /**
   * Handle visibility change
   */
  onVisibilityChange() {
    document.hidden ? this.pause() : this.resume();
  }

  /**
   * Handle before unload
   */
  onBeforeUnload() {
    this.destroy();
  }

  /**
   * Pause application
   */
  pause() {
    if (this.world) {
      const time = this.world.getComponent('time');
      if (time) time.pause();
    }
    this.trigger('paused');
  }

  /**
   * Resume application
   */
  resume() {
    if (this.world) {
      const time = this.world.getComponent('time');
      if (time) time.resume();
    }
    this.trigger('resumed');
  }

  /**
   * Destroy the application
   */
  destroy() {
    document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    window.removeEventListener('beforeunload', this.boundBeforeUnload);

    // Destroy components
    if (this.world) {
      // Make sure to destroy time first to stop animation loop
      const time = this.world.getComponent('time');
      if (time) time.destroy();

      this.world.dispose();
    }
    if (this.smoothScroll) this.smoothScroll.destroy();
    if (this.lazyLoader) this.lazyLoader.destroy();
    if (this.sliders) this.sliders.destroy();

    // Clean up global references
    if (window.app === this) window.app = undefined;

    this.initialized = false;
    this.ready = false;
    this.trigger('destroyed');
  }

  /**
   * Check if application is ready
   * @returns {Boolean} - True if ready
   */
  isReady() {
    return this.ready;
  }
}
